// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/** Client-side behavior for the song index view. */
import type { InitialState } from "../../shared/src/types";
import type { Translator } from "@unilarva/translator";
import type { TranslatorDomBinding } from "@unilarva/translator/dom";
import { renderSongIndexList } from "../../shared/src/render";
import type { SongIndexEntry } from "../../shared/src/song-index";
import type { SongIndexClient } from "./song-index-client";

type IndexSortMode = "title" | "author";
type IndexStatusState = "idle" | "loading" | "message" | "results";

/** Owns lazy loading, sorting, and status UI for the Index pane. */
export class IndexManager {
  private readonly state: InitialState;
  private readonly translator: Translator;
  private readonly translationBinding: TranslatorDomBinding;
  private readonly songIndex: SongIndexClient;
  private readonly indexSortLsKey = "sb_index_sort:v1";

  private activeView = "home";
  private rootEl: HTMLElement | null = null;
  private resultsEl: HTMLElement | null = null;
  private cardEl: HTMLElement | null = null;
  private errorEl: HTMLElement | null = null;
  private errorTextEl: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private statusTextEl: HTMLElement | null = null;
  private entries: readonly SongIndexEntry[] | null = null;
  private loadingPromise: Promise<readonly SongIndexEntry[]> | null = null;
  private statusState: IndexStatusState = "idle";

  constructor(
    state: InitialState,
    translator: Translator,
    translationBinding: TranslatorDomBinding,
    songIndex: SongIndexClient,
  ) {
    this.state = state;
    this.translator = translator;
    this.translationBinding = translationBinding;
    this.songIndex = songIndex;
  }

  /** Called by AppShell whenever the active view changes. */
  handleViewChange(_previousView: string, nextView: string): void {
    this.activeView = nextView;
    if (nextView !== "index") return;

    this.ensureDom();
    this.attachSortToggles();
    this.ensureIndexReady();
  }

  /** Finds current index DOM nodes, including lazily populated panes. */
  private ensureDom(): void {
    if (this.rootEl?.isConnected) return;

    const pane = document.querySelector<HTMLElement>('[data-sb-pane="index"]');
    const root = pane?.querySelector<HTMLElement>("[data-sb-index-root]") ?? null;
    this.rootEl = root;
    this.resultsEl = root?.querySelector<HTMLElement>("[data-sb-index-results]") ?? null;
    this.cardEl = root?.querySelector<HTMLElement>("[data-sb-index-card]") ?? null;
    this.errorEl = root?.querySelector<HTMLElement>("[data-sb-index-error]") ?? null;
    this.errorTextEl = root?.querySelector<HTMLElement>("[data-sb-index-error-text]") ?? null;
    this.statusEl = root?.querySelector<HTMLElement>("[data-sb-index-status]") ?? null;
    this.statusTextEl = root?.querySelector<HTMLElement>("[data-sb-index-status-text]") ?? null;

    const initialMode = this.preferredMode();
    const initialInput = root?.querySelector<HTMLInputElement>(
      `input[data-sb-index-sort="${initialMode}"]`,
    );
    if (initialInput) initialInput.checked = true;

    if (root?.dataset.sbIndexLoaded === "1") this.statusState = "results";
    this.wireProgress();
  }

  /** Wires worker progress once so the status area can show JSON loading progress. */
  private wireProgress(): void {
    const root = this.rootEl;
    if (!root || root.dataset.sbProgressWired === "1") return;

    root.dataset.sbProgressWired = "1";
    this.songIndex.onProgress((loaded, total) => {
      if (this.activeView !== "index") return;
      this.setStatus(
        "loading",
        `${this.translator.translateKey("index.loading")} (${loaded}/${total})`,
      );
    });
  }

  /** Starts loading index entries unless they are already loaded or loading. */
  private ensureIndexReady(): void {
    if (!this.rootEl || !this.resultsEl) return;
    if (this.entries || this.rootEl.dataset.sbIndexLoaded === "1") return;

    if (!this.loadingPromise) {
      this.setStatus("loading");
      this.loadingPromise = this.songIndex
        .getSongIndexEntries()
        .then(entries => {
          this.entries = entries;
          this.render(this.currentMode(), entries);
          return entries;
        })
        .catch(e => {
          this.setUnavailableMessage(e);
          return [];
        })
        .finally(() => {
          this.loadingPromise = null;
        });
    }
  }

  /** Adds change listeners to the title/author sort toggle inputs. */
  private attachSortToggles(): void {
    const root = this.rootEl;
    if (!root) return;

    root.querySelectorAll<HTMLInputElement>("input[data-sb-index-sort]").forEach(input => {
      if (input.dataset.sbWired === "1") return;
      input.dataset.sbWired = "1";
      input.addEventListener("change", () => {
        if (!input.checked) return;
        const mode = input.value === "author" ? "author" : "title";
        this.saveMode(mode);

        if (this.entries) {
          this.render(mode, this.entries);
          return;
        }

        void this.songIndex
          .getSongIndexEntries()
          .then(entries => {
            this.entries = entries;
            this.render(mode, entries);
          })
          .catch(e => this.setUnavailableMessage(e));
      });
    });
  }

  /** Renders the song list and updates the status text to the result count. */
  private render(mode: IndexSortMode, entries: readonly SongIndexEntry[]): void {
    if (!this.rootEl || !this.resultsEl) return;

    this.resultsEl.innerHTML = renderSongIndexList(this.translator, entries, mode);
    this.translationBinding.update(this.resultsEl);
    this.rootEl.dataset.sbIndexLoaded = "1";
    if (this.cardEl) this.cardEl.dataset.sbHasResults = "1";
    this.hideError();
    this.setResultStatus(entries.length);
  }

  /** Sets the status UI without creating extra status elements. */
  private setStatus(state: IndexStatusState, message?: string, i18nKey?: string): void {
    this.statusState = state;
    const status = this.statusEl;
    const text = this.statusTextEl;
    if (!status || !text) return;

    const spinnerOn = state === "loading";
    const visible = spinnerOn || !!message?.trim();

    status.classList.toggle("sb-search-status--visible", visible);
    status.classList.toggle("sb-search-status--no-spinner", !spinnerOn);

    if (state === "loading" && message == null) {
      text.setAttribute("data-i18n", "index.loading");
      this.translationBinding.updateElement(text);
    } else if (message != null) {
      text.textContent = message;
      if (i18nKey) text.setAttribute("data-i18n", i18nKey);
      else text.removeAttribute("data-i18n");
    } else {
      text.textContent = "";
      text.removeAttribute("data-i18n");
    }
  }

  /** Sets a status from an i18n key, falling back to the original error when missing. */
  private setTranslatedStatus(state: IndexStatusState, key: string, error?: unknown): void {
    const translated = this.translator.translateKey(key).trim();
    const message =
      translated && translated !== key
        ? translated
        : error instanceof Error
          ? error.message
          : error != null
            ? String(error)
            : "";
    this.setStatus(state, message, key);
  }

  /** Shows the translated offline/unavailable message in the existing status area. */
  private setUnavailableMessage(error: unknown): void {
    this.showTranslatedError("index.unavailable-offline", error);
  }

  /** Shows a translated persistent error above the normal index status area. */
  private showTranslatedError(key: string, error?: unknown): void {
    const el = this.errorEl;
    const textEl = this.errorTextEl;
    if (!el || !textEl) return;
    this.setStatus("idle");

    const translated = this.translator.translateKey(key).trim();
    const message =
      translated && translated !== key
        ? translated
        : error instanceof Error
          ? error.message
          : error != null
            ? String(error)
            : "";

    textEl.textContent = message;
    textEl.setAttribute("data-i18n", key);
    el.hidden = false;
    el.classList.add("sb-search-status--visible", "sb-search-status--no-spinner");
  }

  /** Hides the persistent index error once index data becomes available. */
  private hideError(): void {
    if (this.errorEl) this.errorEl.hidden = true;
    if (this.errorTextEl) {
      this.errorTextEl.textContent = "";
      this.errorTextEl.removeAttribute("data-i18n");
    }
  }

  /** Renders the result count while preserving a separately translatable label. */
  private setResultStatus(count: number): void {
    this.setStatus("results", String(count));

    const text = this.statusTextEl;
    if (!text) return;

    const key = "index.results.status-label";
    const countElement = text.ownerDocument.createElement("span");
    countElement.textContent = String(count);
    const labelElement = text.ownerDocument.createElement("span");
    labelElement.setAttribute("data-i18n", key);
    labelElement.textContent = this.translator.translateKey(key);
    text.replaceChildren(countElement, " ", labelElement);
    this.translationBinding.updateElement(labelElement);
  }

  private currentMode(): IndexSortMode {
    const checked = this.rootEl?.querySelector<HTMLInputElement>(
      "input[data-sb-index-sort]:checked",
    );
    return checked?.value === "author" ? "author" : "title";
  }

  private preferredMode(): IndexSortMode {
    try {
      return window.localStorage.getItem(this.indexSortLsKey) === "author" ? "author" : "title";
    } catch {
      return "title";
    }
  }

  private saveMode(mode: IndexSortMode): void {
    try {
      window.localStorage.setItem(this.indexSortLsKey, mode);
    } catch {
      // Ignore storage errors.
    }
  }
}
